import {RouteLang} from "~~/route/enum/route-lang.enum";

export const useSchemaOrg = () => {
    const config = useRuntimeConfig().public
    const {locale, t} = useI18n();
    const route = useRoute();
    const {collectionUri} = useI18nCollection();
    const {getAllButIndex} = usePage();

    const baseUrl = config.siteUrl || 'https://clemkerato.be'

    const { data: servicesData } = useAsyncData(
        `schema-org-services-${locale.value}`,
        () => queryCollection(collectionUri('services')).all()
    );
    const services = computed(() => getAllButIndex(servicesData.value ?? []));
    const organizationSchema = {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        '@id': `${baseUrl}/#organization`,
        name: config.siteName,
        url: baseUrl,
        logo: `${baseUrl}${config.logo.horizontal}`,
        description: t('seo.title'),
        address: {
            '@type': 'PostalAddress',
            streetAddress: config.contact.address.street,
            addressLocality: config.contact.address.city,
            postalCode: config.contact.address.postcode,
            addressCountry: config.contact.address.country
        },
        contactPoint: {
            '@type': 'ContactPoint',
            telephone: config.contact.phone,
            email: config.contact.email,
            contactType: 'customer service',
            availableLanguage: Object.values(RouteLang).map(l => l.toLowerCase())
        },
        sameAs: [
            config.socials.facebook,
            config.socials.instagram
        ]
    }
    // LocalBusiness schema
    const localBusinessSchema = computed(() => ({
        '@context': 'https://schema.org',
        '@type': ["LocalBusiness", "HealthAndBeautyBusiness", "BeautySalon"],        '@id': `${baseUrl}/#localbusiness`,
        name: config.siteName,
        image: `${baseUrl}/img/local-business-clemkerato.jpg`,
        telephone: config.contact.phone,
        email: config.contact.email,
        url: baseUrl,
        address: {
            '@type': 'PostalAddress',
            streetAddress: config.contact.address.street,
            addressLocality: config.contact.address.city,
            postalCode: config.contact.address.postcode,
            addressCountry: config.contact.address.country
        },
        geo: {
            '@type': 'GeoCoordinates',
            latitude: 50.69398978908971,
            longitude: 4.609753764423207
        },
        openingHoursSpecification: [
            {
                ...config.openingHoursSpecification
            }
        ],
        priceRange: '$$',
        currenciesAccepted: 'EUR',
        paymentAccepted: 'Cash, Credit Card, Bancontact',
        areaServed: {
            '@type': 'GeoCircle',
            geoMidpoint: {
                '@type': 'GeoCoordinates',
                latitude: 50.69398978908971,
                longitude: 4.609753764423207
            },
            geoRadius: '30000'
        },
        hasOfferCatalog: {
            '@type': 'OfferCatalog',
            name: t('seo.offerCatalogName'),
            itemListElement:
                    services.value.map(service => ({
                        '@type': 'Offer',
                        itemOffered: {
                            '@type': 'Service',
                            name: service.title,
                            description: service.description,
                            url: service.meta?.booking_url,
                        }
                    }))
        }
    }))
    // WebSite schema with search action
    const websiteSchema = {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        '@id': `${baseUrl}/#website`,
        url: baseUrl,
        name: config.siteName,
        description: t('seo.title'),
        inLanguage: Object.values(RouteLang).map(l => l.toLowerCase()),
        publisher: {
            '@id': `${baseUrl}/#organization`
        }
    }
    // WebPage schema
    const webPageSchema = (title: string, description: string) => ({
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        '@id': `${baseUrl}${route.fullPath}#webpage`,
        url: `${baseUrl}${route.fullPath}`,
        name: title,
        description: description,
        isPartOf: {
            '@id': `${baseUrl}/#website`
        },
        inLanguage: locale.value,
        dateModified: new Date().toISOString().split('T')[0]
    })
    // BreadcrumbList schema
    const breadcrumbSchema = (items: { name: string; url: string }[]) => ({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: items.map((item, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: item.name,
            item: `${baseUrl}${item.url}`
        }))
    })
    const addSchemas = (schemas: (object | Ref<object>)[]) => {
        useHead(computed(() => ({
            script: schemas.map(schema => ({
                type: 'application/ld+json',
                innerHTML: JSON.stringify(isRef(schema) ? schema.value : schema)
            }))
        })))
    }
    return {
        organizationSchema,
        localBusinessSchema,
        websiteSchema,
        webPageSchema,
        breadcrumbSchema,
        addSchemas
    }
}
